import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  FillingBatchIngredient,
  FillingBatchIngredientsState,
  CreateFillingBatchIngredientRequest,
  UpdateFillingBatchIngredientRequest
} from '../../types/fillingBatchIngredient';
import { fillingBatchIngredientApiService } from '../../services/fillingBatchIngredientApiService';

// Async thunks

/**
 * Fetch all filling batch ingredients from the API
 */
export const fetchAllFillingBatchIngredients = createAsyncThunk(
  'fillingBatchIngredients/fetchAll',
  async () => {
    return await fillingBatchIngredientApiService.fetchAllFillingBatchIngredients();
  }
);

/**
 * Fetch filling batch ingredients for a specific filling batch
 */
export const fetchFillingBatchIngredientsByFillingBatchId = createAsyncThunk(
  'fillingBatchIngredients/fetchByFillingBatchId',
  async (fillingBatchId: string) => {
    return await fillingBatchIngredientApiService.fetchFillingBatchIngredientsByFillingBatchId(fillingBatchId);
  }
);

/**
 * Create a new filling batch ingredient
 */
export const createFillingBatchIngredient = createAsyncThunk(
  'fillingBatchIngredients/create',
  async (ingredient: CreateFillingBatchIngredientRequest) => {
    return await fillingBatchIngredientApiService.createFillingBatchIngredient(ingredient);
  }
);

/**
 * Update an existing filling batch ingredient
 */
export const updateFillingBatchIngredient = createAsyncThunk(
  'fillingBatchIngredients/update',
  async ({ id, data }: { id: string; data: UpdateFillingBatchIngredientRequest }) => {
    return await fillingBatchIngredientApiService.updateFillingBatchIngredient(id, data);
  }
);

/**
 * Delete a filling batch ingredient
 */
export const deleteFillingBatchIngredient = createAsyncThunk(
  'fillingBatchIngredients/delete',
  async (id: string) => {
    await fillingBatchIngredientApiService.deleteFillingBatchIngredient(id);
    return id;
  }
);

// Initial state
const initialState: FillingBatchIngredientsState = {
  ingredients: [],
  loading: false,
  error: null,
};

const fillingBatchIngredientSlice = createSlice({
  name: 'fillingBatchIngredients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch all filling batch ingredients
    builder
      .addCase(fetchAllFillingBatchIngredients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllFillingBatchIngredients.fulfilled, (state, action) => {
        state.loading = false;
        state.ingredients = action.payload;
      })
      .addCase(fetchAllFillingBatchIngredients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch filling batch ingredients';
      });

    // Fetch by filling batch ID
    builder
      .addCase(fetchFillingBatchIngredientsByFillingBatchId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFillingBatchIngredientsByFillingBatchId.fulfilled, (state, action) => {
        state.loading = false;
        // Replace ingredients for this filling batch
        const fillingBatchId = action.payload[0]?.fillingBatchId;
        if (fillingBatchId) {
          state.ingredients = [
            ...state.ingredients.filter(i => i.fillingBatchId !== fillingBatchId),
            ...action.payload
          ];
        }
      })
      .addCase(fetchFillingBatchIngredientsByFillingBatchId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch filling batch ingredients';
      });

    // Create filling batch ingredient
    builder
      .addCase(createFillingBatchIngredient.fulfilled, (state, action) => {
        state.ingredients.push(action.payload);
      })
      .addCase(createFillingBatchIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create filling batch ingredient';
      });

    // Update filling batch ingredient
    builder
      .addCase(updateFillingBatchIngredient.fulfilled, (state, action) => {
        const index = state.ingredients.findIndex(i => i.id === action.payload.id);
        if (index !== -1) {
          state.ingredients[index] = action.payload;
        }
      })
      .addCase(updateFillingBatchIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update filling batch ingredient';
      });

    // Delete filling batch ingredient
    builder
      .addCase(deleteFillingBatchIngredient.fulfilled, (state, action) => {
        state.ingredients = state.ingredients.filter(i => i.id !== action.payload);
      })
      .addCase(deleteFillingBatchIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete filling batch ingredient';
      });
  }
});

export const { clearError } = fillingBatchIngredientSlice.actions;
export default fillingBatchIngredientSlice.reducer;
